export type LockScopeType = 'field' | 'subsection' | 'section' | 'borrower' | 'application' | 'page' | 'page_section'

export interface IFieldLockScope {
  type: 'field'
  borrowerSeperator: string
  fieldOrigin: string
  fieldKey: string
  parentSubsection?: string
}

export interface ISubsectionLockScope {
  type: 'subsection'
  borrowerSeperator: string
  fieldOrigin: string
  fieldKey: string
}

export interface ISectionLockScope {
  type: 'section'
  borrowerSeperator: string
  fieldOrigin: string
}

export interface IBorrowerLockScope {
  type: 'borrower'
  borrowerSeperator: string
}

export interface IApplicationLockScope {
  type: 'application'
}

export interface IPageLockScope {
  type: 'page'
  pageId: string
}

export interface IPageSectionLockScope {
  type: 'page_section'
  pageId: string
  fieldOrigin: string
}

export type LockScope =
  | IFieldLockScope
  | ISubsectionLockScope
  | ISectionLockScope
  | IBorrowerLockScope
  | IApplicationLockScope
  | IPageLockScope
  | IPageSectionLockScope

export type LockState = Record<string, 0 | 1>

export function lockScopeToKey(scope: LockScope): string {
  switch (scope.type) {
    case 'application':
      return 'application'
    case 'borrower':
      return `borrower:${scope.borrowerSeperator}`
    case 'section':
      return `section:${scope.borrowerSeperator}:${scope.fieldOrigin}`
    case 'subsection':
      return `subsection:${scope.borrowerSeperator}:${scope.fieldOrigin}:${scope.fieldKey}`
    case 'field':
      return `field:${scope.borrowerSeperator}:${scope.fieldOrigin}:${scope.fieldKey}`
    case 'page':
      return `page:${scope.pageId}`
    case 'page_section':
      return `page_section:${scope.pageId}:${scope.fieldOrigin}`
  }
}

export function isExplicitlyUnlocked(locks: LockState, scope: LockScope): boolean {
  const key = lockScopeToKey(scope)
  if (locks[key] === 0) return true
  if (locks[key] === 1) return false

  switch (scope.type) {
    case 'field': {
      if (scope.parentSubsection) {
        const subsectionKey = lockScopeToKey({
          type: 'subsection',
          borrowerSeperator: scope.borrowerSeperator,
          fieldOrigin: scope.fieldOrigin,
          fieldKey: scope.parentSubsection,
        })
        if (locks[subsectionKey] === 0) return true
        if (locks[subsectionKey] === 1) return false
      }
      const sectionKey = lockScopeToKey({
        type: 'section',
        borrowerSeperator: scope.borrowerSeperator,
        fieldOrigin: scope.fieldOrigin,
      })
      if (locks[sectionKey] === 0) return true
      if (locks[sectionKey] === 1) return false
      const borrowerKey = lockScopeToKey({ type: 'borrower', borrowerSeperator: scope.borrowerSeperator })
      if (locks[borrowerKey] === 0) return true
      if (locks[borrowerKey] === 1) return false
      if (locks['application'] === 0) return true
      return false
    }
    case 'subsection': {
      const sectionKey = lockScopeToKey({
        type: 'section',
        borrowerSeperator: scope.borrowerSeperator,
        fieldOrigin: scope.fieldOrigin,
      })
      if (locks[sectionKey] === 0) return true
      if (locks[sectionKey] === 1) return false
      const borrowerKey = lockScopeToKey({ type: 'borrower', borrowerSeperator: scope.borrowerSeperator })
      if (locks[borrowerKey] === 0) return true
      if (locks[borrowerKey] === 1) return false
      if (locks['application'] === 0) return true
      return false
    }
    case 'section': {
      const borrowerKey = lockScopeToKey({ type: 'borrower', borrowerSeperator: scope.borrowerSeperator })
      if (locks[borrowerKey] === 0) return true
      if (locks[borrowerKey] === 1) return false
      if (locks['application'] === 0) return true
      return false
    }
    case 'borrower': {
      return locks['application'] === 0
    }
    case 'page_section': {
      const pageKey = lockScopeToKey({ type: 'page', pageId: scope.pageId })
      return locks[pageKey] === 0
    }
    default:
      return false
  }
}

export function isEffectivelyLocked(locks: LockState, scope: LockScope): boolean {
  const key = lockScopeToKey(scope)
  if (locks[key] === 0) return false
  if (locks[key] === 1) return true

  switch (scope.type) {
    case 'field': {
      if (scope.parentSubsection) {
        const subsectionKey = lockScopeToKey({
          type: 'subsection',
          borrowerSeperator: scope.borrowerSeperator,
          fieldOrigin: scope.fieldOrigin,
          fieldKey: scope.parentSubsection,
        })
        if (locks[subsectionKey] === 0) return false
        if (locks[subsectionKey] === 1) return true
      }
      const sectionKey = lockScopeToKey({
        type: 'section',
        borrowerSeperator: scope.borrowerSeperator,
        fieldOrigin: scope.fieldOrigin,
      })
      if (locks[sectionKey] === 0) return false
      if (locks[sectionKey] === 1) return true
      const borrowerKey = lockScopeToKey({ type: 'borrower', borrowerSeperator: scope.borrowerSeperator })
      if (locks[borrowerKey] === 0) return false
      if (locks[borrowerKey] === 1) return true
      return locks['application'] === 1
    }
    case 'subsection': {
      const sectionKey = lockScopeToKey({
        type: 'section',
        borrowerSeperator: scope.borrowerSeperator,
        fieldOrigin: scope.fieldOrigin,
      })
      if (locks[sectionKey] === 0) return false
      if (locks[sectionKey] === 1) return true
      const borrowerKey = lockScopeToKey({ type: 'borrower', borrowerSeperator: scope.borrowerSeperator })
      if (locks[borrowerKey] === 0) return false
      if (locks[borrowerKey] === 1) return true
      return locks['application'] === 1
    }
    case 'section': {
      const borrowerKey = lockScopeToKey({ type: 'borrower', borrowerSeperator: scope.borrowerSeperator })
      if (locks[borrowerKey] === 0) return false
      if (locks[borrowerKey] === 1) return true
      return locks['application'] === 1
    }
    case 'borrower': {
      return locks['application'] === 1
    }
    case 'page_section': {
      const pageKey = lockScopeToKey({ type: 'page', pageId: scope.pageId })
      if (locks[pageKey] === 0) return false
      return locks[pageKey] === 1
    }
    default:
      return false
  }
}

export interface ScopeColumns {
  scopeType: string
  borrowerSeperator: string
  fieldOrigin: string
  fieldKey: string
}

export function scopeToColumns(scope: LockScope): ScopeColumns {
  switch (scope.type) {
    case 'application':
      return { scopeType: 'application', borrowerSeperator: '', fieldOrigin: '', fieldKey: '' }
    case 'borrower':
      return { scopeType: 'borrower', borrowerSeperator: scope.borrowerSeperator, fieldOrigin: '', fieldKey: '' }
    case 'section':
      return {
        scopeType: 'section',
        borrowerSeperator: scope.borrowerSeperator,
        fieldOrigin: scope.fieldOrigin,
        fieldKey: '',
      }
    case 'subsection':
      return {
        scopeType: 'subsection',
        borrowerSeperator: scope.borrowerSeperator,
        fieldOrigin: scope.fieldOrigin,
        fieldKey: scope.fieldKey,
      }
    case 'field':
      return {
        scopeType: 'field',
        borrowerSeperator: scope.borrowerSeperator,
        fieldOrigin: scope.fieldOrigin,
        fieldKey: scope.fieldKey,
      }
    case 'page':
      return { scopeType: 'page', borrowerSeperator: scope.pageId, fieldOrigin: '', fieldKey: '' }
    case 'page_section':
      return {
        scopeType: 'page_section',
        borrowerSeperator: scope.pageId,
        fieldOrigin: scope.fieldOrigin,
        fieldKey: '',
      }
  }
}
